import type { ApplicableVehicle } from '@web/vin-sessions/types';
import type { StisFilterValues } from './stis-inline-filters';

export type TrimOption = { vehicleId: string; label: string };

export type VinError = 'format';

export type FilterChip = {
    key: string;
    extra?: number;
    group: 'type' | 'vin' | 'year' | 'carline' | 'trim' | 'publishedYear' | 'publishedMonth';
    label: string;
};

export const formatTrimLabel = (vehicle: ApplicableVehicle): string =>
    [vehicle.vehicleEngine, vehicle.vehicleTransmission, vehicle.vehicleDrivetrain]
        .map((part) => part?.description?.trim())
        .filter(Boolean)
        .join(', ');

export const toTrimOptions = (vehicles: ApplicableVehicle[]): TrimOption[] => {
    const seen = new Set<string>();
    return vehicles
        .map((vehicle) => ({ vehicleId: vehicle.vehicleId, label: formatTrimLabel(vehicle) }))
        .filter((option) => {
            if (!option.label || seen.has(option.vehicleId)) {
                return false;
            }
            seen.add(option.vehicleId);
            return true;
        })
        .sort((a, b) => a.label.localeCompare(b.label));
};

export const normalizeVin = (raw: string): string => raw.replace(/[\s-]/g, '').toUpperCase();

export const isFullVin = (vin: string): boolean => /^[A-HJ-NPR-Z0-9]{17}$/.test(vin);

const TECHTIPS_TYPE = 'techtips newsletter';

const FIRST_TECHTIPS_YEAR = 1991;

export const isTechTipsLabel = (label?: string): boolean => label?.trim().toLowerCase() === TECHTIPS_TYPE;

const SERVICE_DIAGNOSTICS_TYPE = 'service diagnostics';

export const isServiceDiagnosticsLabel = (label?: string): boolean =>
    label?.trim().toLowerCase() === SERVICE_DIAGNOSTICS_TYPE;

export const hasTechTips = (publicationTypes: { publicationType: string }[] = []): boolean =>
    publicationTypes.some((type) => isTechTipsLabel(type?.publicationType));

const ACCESSORY_TYPE_PREFIX = 'accessory installation';

export const isAccessoryType = (type?: { publicationType: string }): boolean =>
    Boolean(type?.publicationType?.trim().toLowerCase().startsWith(ACCESSORY_TYPE_PREFIX));

export const isAccessoryOnly = (publicationTypes: { publicationType: string }[] = []): boolean =>
    publicationTypes.length > 0 && publicationTypes.every(isAccessoryType);

export const publishedYearOptions = (now: Date = new Date()): string[] =>
    Array.from({ length: now.getFullYear() - FIRST_TECHTIPS_YEAR + 1 }, (_, index) =>
        String(now.getFullYear() - index)
    );

export const formatPublishedMonth = (month: string): string =>
    new Date(2000, Number(month) - 1, 1).toLocaleString('en-US', { month: 'long' });

export const PUBLISHED_MONTHS: string[] = Array.from({ length: 12 }, (_, index) => String(index + 1));

export const toSearchFilters = (values: StisFilterValues): StisFilterValues => {
    if (!hasTechTips(values.publicationTypes)) {
        return { ...values, publishedYear: undefined, publishedMonth: undefined };
    }
    const withoutVehicle = { ...values, modelYear: undefined, carline: [], trim: undefined, vin: undefined };
    return values.publicationTypes.some((type) => !hasTechTips([type]))
        ? { ...withoutVehicle, publishedYear: undefined, publishedMonth: undefined }
        : withoutVehicle;
};

export const countAppliedFilters = (values: Partial<StisFilterValues>): number => {
    const types = (values.publicationTypes ?? []).length;
    return (
        types +
        (values.vin ? 1 : 0) +
        (values.modelYear ? 1 : 0) +
        ((values.carline ?? []).length > 0 ? 1 : 0) +
        (values.trim ? 1 : 0) +
        (values.publishedYear ? 1 : 0) +
        (values.publishedMonth ? 1 : 0)
    );
};

export const buildFilterChips = (values: Partial<StisFilterValues>): FilterChip[] => {
    const types = values.publicationTypes ?? [];
    const chips: FilterChip[] =
        types.length > 0
            ? [{ key: 'type', group: 'type', label: types[0].publicationType, extra: types.length - 1 }]
            : [];
    if (values.vin) {
        chips.push({ key: 'vin', group: 'vin', label: values.vin });
    }
    if (values.modelYear) {
        chips.push({ key: 'year', group: 'year', label: String(values.modelYear) });
    }
    if (values.carline && values.carline.length > 0) {
        chips.push({ key: 'carline', group: 'carline', label: values.carline[0].description });
    }
    if (values.trim) {
        chips.push({ key: 'trim', group: 'trim', label: values.trim.label });
    }
    if (values.publishedYear) {
        chips.push({ key: 'publishedYear', group: 'publishedYear', label: String(values.publishedYear) });
    }
    if (values.publishedMonth) {
        chips.push({
            key: 'publishedMonth',
            group: 'publishedMonth',
            label: formatPublishedMonth(String(values.publishedMonth)),
        });
    }
    return chips;
};
